import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  GqlAgendaItem,
  GqlAgendaItemKind,
  GqlAiCreateInput,
  GqlMeeting,
} from '../../../../../graphql/base-types.graphql';
import { optionalBoardMeetingAgendaItems } from '../../../../board-meetings/recipes';
import { useCreateAiMutation } from '../../../../../graphql/react-operations';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@startuptools/ui/Button';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { BoardMeetingDocument } from '../../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../../shareholders-meetings/ShareholdersMeeting.graphql';

type FormValues = { order: number; ai: number; subjectPrimaryLang: string; subjectSecondaryLang: string };

export const AgendaItemAddDialog = ({
  agendaItems,
  meeting,
  availableRange,
  onClose,
}: {
  agendaItems: Pick<GqlAgendaItem, 'id' | 'kind' | 'lastGroup' | 'firstGroup' | 'order' | 'subjectPrimaryLang'>[];
  meeting: Pick<GqlMeeting, 'id' | 'secondaryLang'>;
  availableRange: number[];
  onClose: () => void;
}) => {
  const companyId = useCompanyId();
  const existing = agendaItems.map(ai => ai.subjectPrimaryLang);
  const proposedAis = optionalBoardMeetingAgendaItems(meeting.id).filter(
    ai => !existing.includes(ai.subjectPrimaryLang),
  );

  // eslint-disable-next-line sut/loading-mutation
  const [doCreateAi] = useCreateAiMutation({ refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument] });
  const { control, register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { order: availableRange[0] },
  });
  const ai = watch('ai');

  const onSubmit: SubmitHandler<FormValues> = async values => {
    let newAgendaItemInput: GqlAiCreateInput;
    if (values.ai < 0) {
      newAgendaItemInput = {
        order: values.order,
        subjectPrimaryLang: values.subjectPrimaryLang,
        subjectSecondaryLang: values.subjectSecondaryLang,
        kind: GqlAgendaItemKind.Standard,
        mandatory: false,
        meetingId: meeting.id,
      };
    } else {
      newAgendaItemInput = { ...proposedAis[values.ai]!, order: values.order };
    }
    await doCreateAi({ variables: { companyId, input: newAgendaItemInput } });

    onClose();
    return null;
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack mt={3} gap={2} minWidth={350}>
            <Typography level="h4">
              <Trans id="Add Agenda Item" />
            </Typography>

            <Controller
              control={control}
              name="order"
              rules={{ required: true }}
              render={({ field: { onChange, ...field } }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Item Number" />
                  </FormLabel>
                  <Select {...field} onChange={(_, value) => onChange(value)}>
                    {availableRange.map(r => (
                      <Option key={r} value={r}>
                        {r}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="ai"
              rules={{ required: true }}
              render={({ field: { onChange, ...field } }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Select Subject" />
                  </FormLabel>
                  <Select {...field} onChange={(_, value) => onChange(value)}>
                    {proposedAis.map((ai, i) => (
                      <Option key={i} value={i}>
                        {ai.subjectPrimaryLang}
                      </Option>
                    ))}
                    <Option value={-1}>
                      <i>
                        <Trans id="Other subject" />
                      </i>
                    </Option>
                  </Select>
                </FormControl>
              )}
            />

            {ai < 0 && (
              <>
                <FormControl required>
                  <FormLabel>
                    <Trans id="Subject" />
                  </FormLabel>
                  <Input {...register('subjectPrimaryLang')} />
                </FormControl>

                {meeting.secondaryLang && (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Subject (eng)" />
                    </FormLabel>
                    <Input {...register('subjectSecondaryLang')} />
                  </FormControl>
                )}
              </>
            )}

            <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
              <Button variant="outlined" color="neutral" onClick={onClose}>
                <Trans id="Cancel" />
              </Button>
              <Button type="submit">
                <Trans id="Save" />
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};
